import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { nome: "", giorni: "", costo: "", attivo: true };
const SHOWCASE_FILTERS = [
  { id: "tutte", label: "Tutte" },
  { id: "attiva", label: "Attive" },
  { id: "scaduta", label: "Scadute" },
];
const euro = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function formatWhen(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

function formatEuro(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  if (number === 0) return "Gratuito";
  return euro.format(number);
}

export default function PacchettiVetrinaAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState("");
  const [open, setOpen] = useState(false);
  const [showcases, setShowcases] = useState([]);
  const [showcasesLoading, setShowcasesLoading] = useState(true);
  const [showcaseFilter, setShowcaseFilter] = useState("tutte");
  const [hidingId, setHidingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/pacchetti-vetrina`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento pacchetti");
      setItems(json.pacchetti || []);
    } catch (error) {
      toast.error(error.message || "Errore pacchetti");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadShowcases = useCallback(async () => {
    setShowcasesLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/vetrine-strutture`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore caricamento vetrine");
      setShowcases(json.strutture || []);
    } catch (error) {
      toast.error(error.message || "Errore vetrine");
      setShowcases([]);
    } finally {
      setShowcasesLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
    loadShowcases();
  }, [load, loadShowcases]);

  function openCreate() {
    setEditingId("");
    setForm(EMPTY);
    setOpen(true);
  }

  function openEdit(item) {
    setEditingId(item.id);
    setForm({
      nome: item.nome || "",
      giorni: item.giorni ?? "",
      costo: item.costo ?? "",
      attivo: item.attivo !== false,
    });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(
        editingId
          ? `${API}/api/admin/casadiriposoperte/pacchetti-vetrina/${editingId}`
          : `${API}/api/admin/casadiriposoperte/pacchetti-vetrina`,
        {
          method: editingId ? "PUT" : "POST",
          headers,
          body: JSON.stringify({
            nome: form.nome,
            giorni: form.giorni,
            costo: form.costo,
            attivo: form.attivo,
          }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Salvataggio non riuscito");
      toast.success(editingId ? "Pacchetto aggiornato" : "Pacchetto creato");
      setOpen(false);
      await load();
    } catch (error) {
      toast.error(error.message || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  }

  async function setCatalog(item, visible) {
    const question = visible
      ? `Rimetti "${item.nome_struttura}" nel catalogo?`
      : `Togliere "${item.nome_struttura}" dal catalogo? La scheda non compare più in ricerca, anche se la vetrina è ancora attiva. Il pacchetto resta valido fino alla scadenza.`;
    if (!window.confirm(question)) return;
    setHidingId(item.zoho_id);
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/vetrine-strutture/catalogo`, {
        method: "POST",
        headers,
        body: JSON.stringify({ zoho_id: item.zoho_id, visibile: visible }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Aggiornamento non riuscito");
      toast.success(json.message || "Catalogo aggiornato");
      await loadShowcases();
    } catch (error) {
      toast.error(error.message || "Aggiornamento non riuscito");
    } finally {
      setHidingId("");
    }
  }

  async function remove(item) {
    if (!window.confirm(`Eliminare il pacchetto "${item.nome}"? Le vetrine già pagate restano attive fino alla scadenza.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/casadiriposoperte/pacchetti-vetrina/${item.id}`, {
        method: "DELETE",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Eliminazione non riuscita");
      toast.success("Pacchetto eliminato");
      await load();
    } catch (error) {
      toast.error(error.message || "Eliminazione non riuscita");
    }
  }

  const visibleShowcases = showcases.filter((item) => showcaseFilter === "tutte" || item.stato === showcaseFilter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-600" />
            Pacchetti vetrina
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Giorni e costo della visibilità sponsorizzata sul catalogo di casadiriposoperte.it.
            Dopo il pagamento la struttura compare in alto nei risultati; a scadenza torna nell&apos;ordine normale.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { load(); loadShowcases(); }} disabled={loading || showcasesLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Nuovo pacchetto
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Nessun pacchetto. Le strutture non possono ancora acquistare la vetrina.</p>
          ) : (
            <div className="divide-y">
              {items.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="font-semibold text-slate-900">{item.nome}</p>
                    <p className="text-sm text-slate-500">
                      {item.giorni} {item.giorni === 1 ? "giorno" : "giorni"} · {formatEuro(item.costo)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{item.attivo === false ? "Sospeso" : "In vendita"}</Badge>
                    <Button size="sm" variant="outline" onClick={() => openEdit(item)}>
                      <Pencil className="w-4 h-4 mr-1.5" />
                      Modifica
                    </Button>
                    <Button size="icon" variant="ghost" title="Elimina" onClick={() => remove(item)}>
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
            <div>
              <h3 className="font-semibold text-slate-900">Strutture con vetrina</h3>
              <p className="text-sm text-slate-500">Pacchetti ancora in corso e pacchetti già scaduti.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {SHOWCASE_FILTERS.map((filter) => (
                <Button
                  key={filter.id}
                  size="sm"
                  variant={showcaseFilter === filter.id ? "default" : "outline"}
                  onClick={() => setShowcaseFilter(filter.id)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
          {showcasesLoading ? (
            <div className="flex items-center gap-2 p-6 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento
            </div>
          ) : visibleShowcases.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Nessuna struttura in questo elenco.</p>
          ) : (
            <div className="divide-y">
              {visibleShowcases.map((item) => (
                <div key={item.zoho_id || item.nome_struttura} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{item.nome_struttura || "Struttura"}</p>
                    <p className="text-sm text-slate-500">
                      {item.email}
                      {item.comune ? ` · ${item.comune}${item.provincia ? ` (${item.provincia})` : ""}` : ""}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {item.pacchetto || "Pacchetto"}
                      {item.stato === "attiva" ? ` · fino al ${formatWhen(item.vetrina_fino)}` : ` · scaduta il ${formatWhen(item.vetrina_fino)}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{item.stato === "attiva" ? "Vetrina attiva" : "Vetrina scaduta"}</Badge>
                    <Badge variant="outline">{item.nel_catalogo ? "Nel catalogo" : "Nascosta"}</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={hidingId === item.zoho_id}
                      onClick={() => setCatalog(item, !item.nel_catalogo)}
                    >
                      {item.nel_catalogo ? "Togli dal catalogo" : "Rimetti nel catalogo"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label htmlFor="vetrina-nome">Nome</Label>
              <Input id="vetrina-nome" value={form.nome} onChange={(event) => setForm((prev) => ({ ...prev, nome: event.target.value }))} placeholder="Vetrina 7 giorni" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="vetrina-giorni">Giorni</Label>
                <Input id="vetrina-giorni" type="number" min="1" max="365" value={form.giorni} onChange={(event) => setForm((prev) => ({ ...prev, giorni: event.target.value }))} />
              </div>
              <div>
                <Label htmlFor="vetrina-costo">Costo (€)</Label>
                <Input id="vetrina-costo" type="number" min="0" step="0.01" value={form.costo} onChange={(event) => setForm((prev) => ({ ...prev, costo: event.target.value }))} />
                <p className="mt-1 text-xs text-slate-500">0 € attiva la vetrina senza pagamento. Un prezzo a pagamento parte da 0,50 €.</p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(event) => setForm((prev) => ({ ...prev, attivo: event.target.checked }))}
              />
              In vendita sul portale strutture
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
